import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type RouterService from '@ember/routing/router';
import type PersonModel from 'tio-common/models/person';
import type Transition from '@ember/routing/transition';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type DashboardRouteModel = Resolved<ReturnType<AuthenticatedDashboardRoute['model']>>;

export default class AuthenticatedDashboardRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  // check for mentorship form submission re-route without passing
  // as a param that needs to be reset to prevent persistence
  beforeModel(transition: Transition) {
    if (transition.to?.queryParams.formSubmitted) {
      const successMessage = this.intl.t('tuition_assistance.mentoring.submit_success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    }
  }

  async model() {
    const personId = this.sessionContext.user.person.id!;

    // Only load accounts.loans - this is what loan-summary widget needs
    // Other employee/company data is already loaded via /me endpoint in session-context
    const person = await this.store.findRecord<PersonModel>('person', personId, {
      include: 'accounts.loans,accounts.postal-address',
      reload: true,
    });

    // isTaOnly uses employees.plan which is already loaded via /me endpoint
    const employees = this.sessionContext.user.employees;
    const isTaOnlyCompany = this.partner.isTaOnly;
    const companyKey = employees.map((e) => e.plan?.companyKey);
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId;
    const isTaOnlyEmployee = taOnlyPlanId ? companyKey.includes(taOnlyPlanId) : false;

    // Get wellness data from parent authenticated route
    const authenticatedModel = this.modelFor('authenticated') as {
      wellnessData: Record<string, unknown> | null;
    };

    return {
      person,
      isTaOnly: isTaOnlyCompany || isTaOnlyEmployee,
      wellnessData: authenticatedModel.wellnessData,
    };
  }
}
